/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { DragDropModule } from '@angular/cdk/drag-drop';
import { ModuleWithProviders, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';

import { CommonInternalSharedModule } from '@gh/common-internal-shared';
import { ApplicationStateFactory, FormSchemaFactory, UserContext } from '@gh/common-shared';
import { V6_BASE_URL, V6_COOKIE_PREFIX, V6_LEADS_ENABLED } from '@gh/config';
import { TYPE_SET } from '@gh/core-type';
import { MonkeyDropListNoMoveDirective } from '@gh/core-ui/monkey/monkey-drop-list-no-move.directive';
import { MonkeyDropListSortingDisabledDirective } from '@gh/core-ui/monkey/monkey-drop-list-sorting-disabled.directive';

import { TaxRateFinderBusinessService, TaxRateFinderDialogComponent } from './accounting';
import {
  AuthNumberBusinessService,
  AuthNumberGenerationDialogComponent,
  AuthNumberRequestComponent,
  AuthNumberRequestDialogComponent,
} from './auth-number';
import {
  CommunicationsBusinessService,
  EmailDialogComponent,
  MeetingDialogComponent,
  SmsDialogComponent,
  SmsViewDialogComponent,
  WebsiteDialogComponent,
} from './communications';
import {
  GoogleDriveButtonComponent,
  SetFilterDialogComponent,
  HlpUpdateListingStatusDialogComponent,
  HlpQuoteCloneDataDialogComponent,
  UnpaidInvoicesDialogComponent,
} from './components';
import {
  ContactFinderBusinessService,
  ContactFinderDialogComponent,
  ContactMenuComponent,
  ContactMenuControlDirective,
  ContactMenuDialogDirective,
} from './contacts';
import { CostCenterFinderBusinessService, CostCenterFinderDialogComponent } from './cost-centers';
import { AllowForSandboxDirective, AllowForViewDirective } from './directives';
import { EDocDetailsBusinessService, EDocDetailsDialogComponent } from './e-docs';
import {
  ComponentItemFinderBusinessService,
  ComponentItemFinderDialogComponent,
  FormulaCalculationService,
  FormulaPropagationService,
  PbComponentItemFinderBusinessService,
  PbComponentItemFinderCcViewComponent,
  PbComponentItemFinderDialogComponent,
  PbComponentItemFinderFullViewComponent,
  SalesTaxRateFinderBusinessService,
  SalesTaxRateFinderDialogComponent,
} from './estimating';
import {
  CommunityFinderBusinessService,
  CSMasterBusinessService,
  CSMasterFinderDialogComponent,
  DictionaryFinderBusinessService,
  EmailCampaignTemplateFinderBusinessService,
  GenericFinderDialogComponent,
  JobFinderBusinessService,
  JobFinderDialogComponent,
  JobSpecTemplateFinderBusinessService,
  JobSpecTemplateFinderDialogComponent,
  MasterZoneFinderBusinessService,
  OfficeFinderBusinessService,
  SalesPersonFinderBusinessService,
  ScheduleTemplateFinderDialogComponent,
  ScheduleTemplatesFinderBusinessService,
} from './finders';
import { FormulaBuilderComponent, FormulaContextLoader } from './formula';
import {
  HousePlanFinderBusinessService,
  HousePlanFinderDialogComponent,
  PbHousePlanFinderDialogComponent,
} from './house-plans';
import {
  PrinterBusinessService,
  PrinterFinderBusinessService,
  PrinterFinderDialogComponent,
} from './printers';
import { RouterParentFocusedIdDirective } from './routing';
import {
  BuilderApplicationService,
  BuilderAuthNumberService,
  BuilderNavigationService,
  ReportService,
  WebSocketService,
  SmsService,
  MaintenanceService,
  V6_EXPERIENCE_CONFIG,
  V6ExperienceService,
} from './services';
import { StageAdvancementNotificationService } from './sales';
import {
  CommonActions,
  FormActionFlows,
  FormActionPipelines,
  FormActions,
  ListActionPipelines,
} from './skeletons';
import { BuilderApplicationStateFactory, BuilderFormSchemaFactory } from './state';
import { SwitchToBusinessService, SwitchToComponent } from './switch-to';
import { BuilderQuantityTypes, UomQuantityFormatPipe, UomQuantityPipe } from './types';
import { UsageDialogBusinessService, UsageDialogComponent } from './usage-dialog';
import {
  BuilderUserContext,
  BuilderUserContextService,
  BuilderUserProfileStore,
  OfficeAccessService,
  SelectOfficeDialogComponent,
  SwitchOfficeDialogComponent,
  ThemeService,
} from './user';
import { UserFinderBusinessService, UserFinderDialogComponent } from './users';
import {
  JobCsCreateDialogComponent,
  JobSpecCreateBusinessService,
  JobCsCreateBusinessService,
  JobSpecCreateDialogComponent,
  JobDetailToolbarComponent,
  JobDetailsReassignDialogComponent,
} from './jobs';
import {
  JobDetailsDrawsTabComponent,
  JobDrawDetailsDrawsFormState,
  JobDetailsDrawsPaymentScheduleSubtabComponent,
  JobDetailsDrawsLendersScheduleSubtabComponent,
  JobDetailsDrawsHistorySubtabComponent,
  JobDetailsDrawsFormComponent,
} from './jobs/components/job-details-draws-tab';
import { JobDetailsModel } from './jobs/services/jobt-details-model.service';
import { CoreUiExtSplitPaneModule } from '@gh/core-ui-ext/split-pane';
import { FilterRestService } from './filter';

@NgModule({
  imports: [
    DragDropModule,
    CommonInternalSharedModule.forChild(),
    CoreUiExtSplitPaneModule,
  ],
  declarations: [
    AllowForViewDirective,
    AllowForSandboxDirective,
    RouterParentFocusedIdDirective,

    AuthNumberRequestDialogComponent,
    AuthNumberRequestComponent,
    AuthNumberGenerationDialogComponent,
    ContactFinderDialogComponent,
    CostCenterFinderDialogComponent,
    FormulaBuilderComponent,
    SwitchToComponent,
    SelectOfficeDialogComponent,
    SwitchOfficeDialogComponent,
    ComponentItemFinderDialogComponent,
    SalesTaxRateFinderDialogComponent,
    UsageDialogComponent,
    HousePlanFinderDialogComponent,
    UserFinderDialogComponent,
    PbHousePlanFinderDialogComponent,
    PbComponentItemFinderDialogComponent,
    SmsDialogComponent,
    EmailDialogComponent,
    MeetingDialogComponent,
    WebsiteDialogComponent,
    SmsViewDialogComponent,
    PbComponentItemFinderCcViewComponent,
    PbComponentItemFinderFullViewComponent,
    CSMasterFinderDialogComponent,
    JobSpecTemplateFinderDialogComponent,
    ScheduleTemplateFinderDialogComponent,
    JobFinderDialogComponent,
    PrinterFinderDialogComponent,
    ContactMenuComponent,
    ContactMenuControlDirective,
    ContactMenuDialogDirective,
    EDocDetailsDialogComponent,
    TaxRateFinderDialogComponent,
    GenericFinderDialogComponent,
    GoogleDriveButtonComponent,
    SetFilterDialogComponent,
    HlpQuoteCloneDataDialogComponent,
    HlpUpdateListingStatusDialogComponent,
    UnpaidInvoicesDialogComponent,
    UomQuantityPipe,
    UomQuantityFormatPipe,

    // monkey patches
    MonkeyDropListSortingDisabledDirective,
    MonkeyDropListNoMoveDirective,
    JobCsCreateDialogComponent,
    JobSpecCreateDialogComponent,
    JobDetailsDrawsTabComponent,
    JobDetailToolbarComponent,
    JobDetailsReassignDialogComponent,
    JobDetailsDrawsFormComponent,
    JobDetailsDrawsHistorySubtabComponent,
    JobDetailsDrawsLendersScheduleSubtabComponent,
    JobDetailsDrawsPaymentScheduleSubtabComponent,
  ],
  exports: [
    CommonInternalSharedModule,

    JobDetailToolbarComponent,
    JobDetailsReassignDialogComponent,

    RouterParentFocusedIdDirective,

    AllowForViewDirective,
    AllowForSandboxDirective,
    AuthNumberRequestComponent,
    FormulaBuilderComponent,
    SwitchToComponent,

    ContactMenuComponent,
    ContactMenuControlDirective,
    ContactMenuDialogDirective,
    GoogleDriveButtonComponent,
    SetFilterDialogComponent,
    HlpQuoteCloneDataDialogComponent,
    HlpUpdateListingStatusDialogComponent,
    UnpaidInvoicesDialogComponent,
    UomQuantityPipe,
    UomQuantityFormatPipe,
  ],
  schemas: [ NO_ERRORS_SCHEMA ],
})
export class BuilderSharedModule {
  static forRoot(): ModuleWithProviders<BuilderSharedModule> {
    return {
      ngModule: BuilderSharedModule,
      providers: [
        { provide: TYPE_SET, useValue: BuilderQuantityTypes, multi: true },
        { provide: BuilderUserContext, useClass: BuilderUserContextService },
        { provide: UserContext, useExisting: BuilderUserContext },
        { provide: ApplicationStateFactory, useExisting: BuilderApplicationStateFactory },
        { provide: FormSchemaFactory, useExisting: BuilderFormSchemaFactory },
        BuilderApplicationStateFactory,
        BuilderApplicationService,
        BuilderNavigationService,
        BuilderUserProfileStore,
        OfficeAccessService,
        BuilderAuthNumberService,
        ReportService,
        AuthNumberBusinessService,
        CommonActions,
        FormActions,
        FormActionFlows,
        FormActionPipelines,
        ListActionPipelines,
        BuilderFormSchemaFactory,
        ContactFinderBusinessService,
        CostCenterFinderBusinessService,
        FormulaContextLoader,
        JobDrawDetailsDrawsFormState,
        SwitchToBusinessService,
        ComponentItemFinderBusinessService,
        SalesTaxRateFinderBusinessService,
        UsageDialogBusinessService,
        HousePlanFinderBusinessService,
        UserFinderBusinessService,
        PbComponentItemFinderBusinessService,
        CommunicationsBusinessService,
        JobSpecTemplateFinderBusinessService,
        ScheduleTemplatesFinderBusinessService,
        JobFinderBusinessService,
        CSMasterBusinessService,
        JobFinderBusinessService,
        PrinterFinderBusinessService,
        PrinterBusinessService,
        FormulaPropagationService,
        FormulaCalculationService,
        EDocDetailsBusinessService,
        TaxRateFinderBusinessService,
        EmailCampaignTemplateFinderBusinessService,
        MasterZoneFinderBusinessService,
        OfficeFinderBusinessService,
        CommunityFinderBusinessService,
        SalesPersonFinderBusinessService,
        DictionaryFinderBusinessService,
        ThemeService,
        WebSocketService,
        MaintenanceService,
        SmsService,
        {
          provide: V6_EXPERIENCE_CONFIG,
          useValue: { leadsEnabled: V6_LEADS_ENABLED, baseUrl: V6_BASE_URL, cookiePrefix: V6_COOKIE_PREFIX },
        },
        V6ExperienceService,
        StageAdvancementNotificationService,
        JobCsCreateBusinessService,
        JobSpecCreateBusinessService,
        JobDetailsModel,
        FilterRestService,
      ],
    };
  }

  static forChild(): ModuleWithProviders<BuilderSharedModule> {
    return {
      ngModule: BuilderSharedModule,
      providers: [],
    };
  }
}
